// Durable record of recordings that the recovery system re-attached. Since
// 2026-07-14 this store is SUPPRESSION MEMORY ONLY — nothing renders it. A
// successful recovery is silent by design (the persistent "Recovered" rows
// wallpapered providers' screens; the transcript on the encounter is the record
// the clinician reviews). Path A (own-device finalless rebuild) and Path B
// (provably-complete cross-device pull) both call markRecentlyRecovered on
// success.
//
// Why the store still exists: a recovered id SUPPRESSES re-recovery. The
// transcript save is async, so the server manifest stays on the pending list
// until transcription finishes (minutes). Without suppression the poll would
// re-pull all the audio (PHI) and re-upload every 60s until then, and the
// "stuck on another device" banner would re-surface for a recording that was
// just recovered. wasRecovered() feeds that exclusion from BOTH the session set
// (covers a failed storage write) and the persisted map (covers a reload,
// until RECOVERED_TTL_MS expires the entry).
//
// Viewer-scoped, recency-capped map of {recordingId: {chatId, startedAt, recoveredAt}},
// mirroring recordingHeartbeat.js / recordingRecoveryDismissState.js. Best-effort:
// any storage failure degrades to "not suppressed" rather than throwing (the
// in-memory session set still covers this page session).
import { recordingViewerKey } from "./recordingViewerKey";

const STORAGE_PREFIX = "recordingRecovered:";
const MAX_IDS = 50;
// How long a recovered id keeps suppressing. Well past any STT run (so
// suppression never drops while the server row still lags), short enough that
// entries don't accumulate forever now that no UI dismisses them.
export const RECOVERED_TTL_MS = 48 * 60 * 60 * 1000;

// Session-scoped memory of ids recovered by THIS page session; resets on
// reload, by which time the persisted map covers the id.
const sessionRecoveredIds = new Set();

function storageKey() {
  return STORAGE_PREFIX + recordingViewerKey();
}

function readMap() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return {};
    const p = JSON.parse(raw);
    return p && typeof p === "object" ? p : {};
  } catch {
    return {};
  }
}
function writeMap(map) {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_IDS) {
      // Drop the oldest by recoveredAt.
      keys.sort((a, b) => (map[a]?.recoveredAt || 0) - (map[b]?.recoveredAt || 0));
      for (let i = 0; i < keys.length - MAX_IDS; i++) delete map[keys[i]];
    }
    localStorage.setItem(storageKey(), JSON.stringify(map));
  } catch {
    // Quota / private-mode failures are silent — the store is best-effort (the
    // session set still suppresses for this page session).
  }
}

export function markRecentlyRecovered(recordingId, chatId, startedAt) {
  if (!recordingId) return;
  sessionRecoveredIds.add(recordingId);
  const map = readMap();
  // Prune expired entries on write — with no UI reading the store, this is the
  // housekeeping pass that keeps the persisted map from accumulating stale ids.
  const now = Date.now();
  Object.keys(map).forEach((id) => {
    if (now - (map[id]?.recoveredAt || 0) > RECOVERED_TTL_MS) delete map[id];
  });
  map[recordingId] = { chatId, startedAt, recoveredAt: now };
  writeMap(map);
}

// Was this recording recovered here (this session, or persisted and not yet
// expired)? Drives the pending-list exclusion described in the header. The TTL
// is checked HERE, not just at prune time — an entry that outlives its window
// must stop suppressing even if no write has pruned it yet.
export function wasRecovered(recordingId) {
  if (!recordingId) return false;
  if (sessionRecoveredIds.has(recordingId)) return true;
  const entry = readMap()[recordingId];
  return entry != null && Date.now() - (entry.recoveredAt || 0) <= RECOVERED_TTL_MS;
}
